import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthServices } from '../../services/authService';

// Async thunk for logging in
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const userDetail = await AuthServices.login(email, password);
      return userDetail;
    } catch (error) {
      // Handle different error formats
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      } else if (error.message) {
        return rejectWithValue({ message: error.message });
      } else {
        return rejectWithValue({ message: 'Login failed. Please try again.' });
      }
    }
  }
);

// Google OAuth login thunk
export const googleOAuthLogin = createAsyncThunk(
  'auth/googleOAuthLogin',
  async (googleToken, { rejectWithValue }) => {
    try {
      const response = await AuthServices.googleOAuthLogin(googleToken);
      return response;
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      } else if (error.message) {
        return rejectWithValue({ message: error.message });
      } else {
        return rejectWithValue({ message: 'Google login failed. Please try again.' });
      }
    }
  }
);

// Async thunk for refreshing the access token
export const refreshAccessToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { refreshToken } = getState().auth;
      const response = await AuthServices.refreshToken(refreshToken);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthServices.logout();
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const resetPasswordForUser = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const response = await AuthServices.resetPassword(token, password);
      return response;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('access_token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    roles: localStorage.getItem('roles') || "",
    permissions: JSON.parse(localStorage.getItem('permissions')) || [],
    loading: false,
    error: null,
    isPasswordResetSuccessful: [null, ""],
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.roles = null;
      state.permissions = [];
      state.error = null; // Clear errors on logout
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('roles');
      localStorage.removeItem('permissions');
    },
    updateLoggedInUser: (state, action) => {
      state.user = action.payload;
      state.permissions = action.payload.permissions || [];
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    // Add action to clear errors manually
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login pending
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null; // Clear previous errors
      })
      // Login fulfilled
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const userDetail = action.payload.user_detail;
        state.token = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.user = userDetail;
        state.roles = userDetail.role || [];
        state.permissions = userDetail.permissions || [];
        localStorage.setItem('access_token', action.payload.access_token);
        localStorage.setItem('refresh_token', action.payload.refresh_token);
        localStorage.setItem('user', JSON.stringify(action.payload.user_detail));
        localStorage.setItem('roles', userDetail.role || "");
        localStorage.setItem('permissions', JSON.stringify(userDetail.permissions || []));
      })
      // Login rejected
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Store the error
      })
      // Google OAuth pending
      .addCase(googleOAuthLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Google OAuth fulfilled
      .addCase(googleOAuthLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const userDetail = action.payload.user_detail;
        state.token = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.user = userDetail;
        state.roles = userDetail.role || [];
        state.permissions = userDetail.permissions || [];
        localStorage.setItem('access_token', action.payload.access_token);
        localStorage.setItem('refresh_token', action.payload.refresh_token);
        localStorage.setItem('user', JSON.stringify(action.payload.user_detail));
        localStorage.setItem('roles', userDetail.role || "");
        localStorage.setItem('permissions', JSON.stringify(userDetail.permissions || []));
      })
      // Google OAuth rejected
      .addCase(googleOAuthLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.token = action.payload.access_token;
        localStorage.setItem('access_token', action.payload.access_token);
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.roles = null;
        state.permissions = [];
        state.error = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('roles');
        localStorage.removeItem('permissions');
      })
      .addCase(resetPasswordForUser.fulfilled, (state, action) => {
        state.isPasswordResetSuccessful = [true, action.payload.message];
      })
      .addCase(resetPasswordForUser.rejected, (state, action) => {
        state.isPasswordResetSuccessful = [false, action.payload.message];
      });
  }
});

export const { logout, updateLoggedInUser, clearError } = authSlice.actions;
export default authSlice.reducer;